import type {
  ExhibitionArtistData,
  PublicExhibitionData,
  VenueDetailData,
} from '../dto/EntityRepository';

export interface InterestExhibitionCardData {
  key: string;
  venueId: string;
  venueCode: string;
  venueName: string;
  venueNameEn: string;
  venueInstagramUrl: string;
  venueNaverMapUrl: string;
  venueTimeZone: string;
  status: InterestExhibitionStatus;
  exhibition: PublicExhibitionData;
}

export type InterestExhibitionStatus = 'CURRENT' | 'UPCOMING' | 'PAST';

const DEFAULT_VENUE_TIME_ZONE = 'Asia/Seoul';
const DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

const getDateFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const normalizedTimeZone = timeZone.trim() || DEFAULT_VENUE_TIME_ZONE;
  const cached = DATE_FORMATTERS.get(normalizedTimeZone);
  if (cached) return cached;

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: normalizedTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    DATE_FORMATTERS.set(normalizedTimeZone, formatter);
    return formatter;
  } catch {
    return getDateFormatter(DEFAULT_VENUE_TIME_ZONE);
  }
};

const getDateInTimeZone = (date: Date, timeZone: string): string => {
  const parts = getDateFormatter(timeZone).formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes): string => (
    parts.find((part) => part.type === type)?.value ?? ''
  );
  return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
};

export const getInterestExhibitionStatus = (
  exhibition: Pick<PublicExhibitionData, 'startDate' | 'endDate'>,
  timeZone: string,
  now = new Date(),
): InterestExhibitionStatus => {
  const today = getDateInTimeZone(now, timeZone);
  if (exhibition.startDate && exhibition.startDate > today) return 'UPCOMING';
  if (exhibition.endDate && exhibition.endDate < today) return 'PAST';
  return 'CURRENT';
};

const compareInterestExhibitions = (
  a: InterestExhibitionCardData,
  b: InterestExhibitionCardData,
): number => {
  const statusOrder: Record<InterestExhibitionStatus, number> = {
    CURRENT: 0,
    UPCOMING: 1,
    PAST: 2,
  };
  const statusComparison = statusOrder[a.status] - statusOrder[b.status];
  if (statusComparison !== 0) return statusComparison;

  if (a.status === 'PAST') {
    const endDateComparison = b.exhibition.endDate.localeCompare(a.exhibition.endDate);
    if (endDateComparison !== 0) return endDateComparison;
  } else {
    const dateA = a.status === 'CURRENT'
      ? a.exhibition.endDate
      : a.exhibition.startDate;
    const dateB = b.status === 'CURRENT'
      ? b.exhibition.endDate
      : b.exhibition.startDate;
    const dateComparison = dateA.localeCompare(dateB);
    if (dateComparison !== 0) return dateComparison;
  }

  return a.exhibition.id - b.exhibition.id;
};

const normalizeArtist = (artist: ExhibitionArtistData): ExhibitionArtistData => ({
  ...artist,
  artistId: artist.artistId != null && Number(artist.artistId) > 0
    ? Number(artist.artistId)
    : null,
  name: artist.name ?? '',
  nameEn: artist.nameEn ?? '',
  instagramHandle: artist.instagramHandle ?? '',
  architraveHandle: artist.architraveHandle ?? '',
});

const normalizeExhibition = (
  exhibition: PublicExhibitionData,
): PublicExhibitionData => ({
  ...exhibition,
  title: exhibition.title ?? '',
  titleEn: exhibition.titleEn ?? '',
  startDate: exhibition.startDate ?? '',
  endDate: exhibition.endDate ?? '',
  officialUrl: exhibition.officialUrl ?? null,
  artists: (exhibition.artists ?? []).map(normalizeArtist),
});

export const buildInterestExhibitionCardKey = (
  venueId: string,
  exhibitionId: number,
): string => `${encodeURIComponent(venueId)}:${encodeURIComponent(String(exhibitionId))}`;

export const buildInterestExhibitionCards = (
  venue: VenueDetailData | null | undefined,
  now = new Date(),
): InterestExhibitionCardData[] => {
  if (!venue) return [];

  const venueId = String(venue.id ?? '');
  const venueCode = venue.venueCode ?? '';
  const venueName = venue.name ?? '';
  const venueNameEn = venue.nameEn ?? '';
  const venueInstagramUrl = venue.instagramUrl?.trim() ?? '';
  const venueNaverMapUrl = venue.naverMapUrl?.trim() ?? '';
  const venueTimeZone = venue.timeZone?.trim() || DEFAULT_VENUE_TIME_ZONE;

  return (venue.exhibitions ?? []).map((rawExhibition) => {
    const exhibition = normalizeExhibition(rawExhibition);
    return {
      key: buildInterestExhibitionCardKey(venueId, exhibition.id),
      venueId,
      venueCode,
      venueName,
      venueNameEn,
      venueInstagramUrl,
      venueNaverMapUrl,
      venueTimeZone,
      status: getInterestExhibitionStatus(exhibition, venueTimeZone, now),
      exhibition,
    };
  }).sort(compareInterestExhibitions);
};

export const mergeInterestExhibitionCards = (
  current: InterestExhibitionCardData[],
  incoming: InterestExhibitionCardData[],
): InterestExhibitionCardData[] => {
  const seen = new Set<string>();

  return [...current, ...incoming].filter((card) => {
    if (seen.has(card.key)) return false;
    seen.add(card.key);
    return true;
  });
};

export const removeInterestExhibitionCard = (
  cards: InterestExhibitionCardData[],
  key: string,
): InterestExhibitionCardData[] => cards.filter((card) => card.key !== key);

export interface InterestVenueGroupData {
  venueId: string;
  venueCode: string;
  venueName: string;
  venueNameEn: string;
  venueInstagramUrl: string;
  venueNaverMapUrl: string;
  exhibitions: InterestExhibitionCardData[];
}

export const groupInterestExhibitionsByVenue = (
  cards: InterestExhibitionCardData[],
): InterestVenueGroupData[] => {
  const groups: InterestVenueGroupData[] = [];
  const indexByVenueId = new Map<string, number>();

  cards.forEach((card) => {
    const existingIndex = indexByVenueId.get(card.venueId);
    if (existingIndex == null) {
      indexByVenueId.set(card.venueId, groups.length);
      groups.push({
        venueId: card.venueId,
        venueCode: card.venueCode,
        venueName: card.venueName,
        venueNameEn: card.venueNameEn,
        venueInstagramUrl: card.venueInstagramUrl,
        venueNaverMapUrl: card.venueNaverMapUrl,
        exhibitions: [card],
      });
      return;
    }

    groups[existingIndex].exhibitions.push(card);
  });

  return groups;
};

export const removeInterestExhibitionCardsByVenue = (
  cards: InterestExhibitionCardData[],
  venueId: string,
): InterestExhibitionCardData[] => cards.filter((card) => card.venueId !== venueId);

export const getInterestedVenueIds = (
  cards: InterestExhibitionCardData[],
): string[] => {
  const seen = new Set<string>();

  return cards.reduce<string[]>((venueIds, card) => {
    if (!card.venueId || seen.has(card.venueId)) return venueIds;
    seen.add(card.venueId);
    venueIds.push(card.venueId);
    return venueIds;
  }, []);
};

const normalizeMmcaExhibitionUrl = (value: string): string => {
  try {
    const url = new URL(value);
    const isMmcaExhibitionDetail = url.hostname.replace(/^www\./, '') === 'mmca.go.kr'
      && url.pathname === '/exhibitions/exhibitionsDetail.do'
      && url.searchParams.has('exhId');

    if (!isMmcaExhibitionDetail || url.searchParams.get('exhFlag') === '1') {
      return value;
    }

    const params = new URLSearchParams(url.search);
    params.delete('exhFlag');
    url.search = `exhFlag=1&${params.toString()}`;
    return url.href;
  } catch {
    return value;
  }
};

export const getInterestExhibitionTargetUrl = (
  officialUrl: string | null,
  venueInstagramUrl: string,
): string => {
  const trimmedOfficialUrl = officialUrl?.trim();
  if (!trimmedOfficialUrl) return venueInstagramUrl;
  return normalizeMmcaExhibitionUrl(trimmedOfficialUrl);
};

export const formatInterestExhibitionDate = (value: string): string => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  return `${match[1].slice(-2)}.${match[2]}.${match[3]}.`;
};

export const formatInterestExhibitionEndDate = (value: string): string => {
  const formatted = formatInterestExhibitionDate(value);
  return formatted ? `~${formatted}` : '';
};

export const formatInterestExhibitionDateRange = (
  startDate: string,
  endDate: string,
): string => {
  const start = formatInterestExhibitionDate(startDate);
  const end = formatInterestExhibitionDate(endDate);
  if (!start) return end;
  if (!end) return start;
  return `${start} – ${end}`;
};

export interface InterestExhibitionEndDateDisplay {
  text: string;
  dDayText: string;
  isEndingSoon: boolean;
}

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

const parseDate = (value: string): [number, number, number] | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return null;

  return [year, month, day];
};

export const getInterestExhibitionEndDateDisplay = (
  value: string,
  options: { timeZone?: string; now?: Date } = {},
): InterestExhibitionEndDateDisplay => {
  const {
    timeZone = DEFAULT_VENUE_TIME_ZONE,
    now = new Date(),
  } = options;
  const text = formatInterestExhibitionEndDate(value);
  const endDate = parseDate(value);
  if (!endDate) return { text, dDayText: '', isEndingSoon: false };

  const todayParts = getDateFormatter(timeZone).formatToParts(now);
  const getTodayPart = (type: Intl.DateTimeFormatPartTypes): number => (
    Number(todayParts.find((part) => part.type === type)?.value)
  );
  const today = Date.UTC(
    getTodayPart('year'),
    getTodayPart('month') - 1,
    getTodayPart('day'),
  );
  const end = Date.UTC(endDate[0], endDate[1] - 1, endDate[2]);
  const daysRemaining = (end - today) / DAY_IN_MILLISECONDS;
  const isEndingSoon = daysRemaining >= 0 && daysRemaining <= 7;

  return {
    text,
    dDayText: isEndingSoon ? `(D-${daysRemaining})` : '',
    isEndingSoon,
  };
};
